import { KeyOutlined, UserOutlined } from '@ant-design/icons';
import { usePutUtentiIntegraInfoBase } from '@framework/api-client';
import { AuthActions, useCurrentUser } from '@framework/auth';
import {
  FormErrorsAlert,
  FormHandler,
  FormItem,
  SubmitButton,
  TextInput,
} from '@framework/form';
import { useTranslation } from '@framework/i18n';
import {
  DividerTitleElement,
  getNetworkErrorMessage,
  LinkButton,
  useBreadcrumbItem,
} from '@framework/ui';
import { Card, message, Space } from 'antd';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { LegalEntityProfileSection } from '../../components/legal-entity/LegalEntityProfileSection';
import { useSelectedLegalEntity } from '../../components/legal-entity/useSelectedLegalEntity';
import { UserProfileFieldsForm } from '../../components/user/citizen/UserProfileFieldsForm';
import { PageLayout } from '../../ui/layout/PageLayout';
import { CitizenEditSchema } from '../login/schema/CitizenEditSchema';

const StyledH1Title = styled.h1`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 0;
`;
export const StyledH2Title = styled.h2`
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 0;
`;

export interface IUserProfilePageProps {}

export function UserProfilePage(props: IUserProfilePageProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useCurrentUser();
  const { selectedLegalEntity } = useSelectedLegalEntity();

  const userData = useMemo(
    () => ({
      ...user?.utenteLoggato,
    }),
    [user],
  );

  const editMutation = usePutUtentiIntegraInfoBase();

  useBreadcrumbItem({
    path: '/utente/profilo',
    title: t('user.breadcrumb.profilo', 'Profilo'),
  });

  const handleEdit = async (values: any) => {
    try {
      await editMutation.mutateAsync({
        data: {
          idUtente: userData.idUtente,
          ...values,
        },
      });

      // Aggiorniamo le informazioni dell'utente corrente
      dispatch(
        AuthActions.currentLoaded({
          current: {
            ...user,
            utenteLoggato: { ...user?.utenteLoggato, ...values },
          },
        }),
      );

      message.success(
        t(
          'user.profile.update-success',
          'Profilo utente aggiornato con successo.',
        ),
      );
    } catch (error) {
      console.error('Error updating user profile:', error);
      message.error(
        getNetworkErrorMessage(
          error,
          t(
            'user.profile.update-error',
            "Errore durante l'aggiornamento del profilo utente.",
          ),
        ),
      );
    }
  };

  // Se l'utente è extraEU, allora ha un'utenza username+password
  const isExtraEU = userData.estero === true;

  return (
    <PageLayout>
      <div style={{ paddingTop: '20px', paddingBottom: '50px' }}>
        <Card
          loading={!user}
          title={
            <StyledH1Title>
              {t('user.card-title.profilo-utente', 'Profilo Utente')}
            </StyledH1Title>
          }
        >
          {selectedLegalEntity ? (
            <LegalEntityProfileSection
              entity={selectedLegalEntity}
              operator={userData}
            />
          ) : (
            <FormHandler
              schema={CitizenEditSchema}
              onSubmit={handleEdit}
              initialValues={userData}
            >
              <FormErrorsAlert />
              {/* Form Utente */}
              <DividerTitleElement
                orientation="center"
                color="secondary"
                title={
                  <b>
                    {t('user.registration.form.top-title', 'Dati di accesso')}
                  </b>
                }
              />

              <FormItem label="Email" name="email">
                <TextInput
                  prefix={
                    <UserOutlined
                      aria-hidden="true"
                      style={{ color: '#757575' }}
                    />
                  }
                  name="email"
                  readOnly
                />
              </FormItem>

              <UserProfileFieldsForm
                readonly={!isExtraEU}
                hideExtraUEFields={!isExtraEU}
              />

              {isExtraEU && (
                <Space>
                  <LinkButton
                    size="large"
                    style={{ width: '100%' }}
                    type="default"
                    to="/utente/cambia-password"
                  >
                    <KeyOutlined />
                    {t('user.change-password', 'Cambia Password')}
                  </LinkButton>

                  <SubmitButton
                    pristineMessage={null}
                    style={{ width: '100%' }}
                  >
                    {t('user.profile.form.submit-button', 'Salva Modifiche')}
                  </SubmitButton>
                </Space>
              )}
            </FormHandler>
          )}
        </Card>
      </div>
    </PageLayout>
  );
}
